import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Layers, ShieldCheck, FileCheck2, Boxes, Clock, DollarSign, ArrowRight } from 'lucide-react';

const whatWeDo = [
  {
    icon: Layers,
    title: 'Subcontracting & Team Augmentation',
    desc: 'We act as an extension of your primary development firm, adopting your internal compliance, communication protocols, and project workflows seamlessly.',
  },
  {
    icon: Boxes,
    title: 'Enterprise-Grade SaaS Front-Ends',
    desc: 'From multi-tenant dashboards to intricate data visualization layers, we build scalable, secure, and accessible front-ends that match your backend strength.',
  },
  {
    icon: FileCheck2,
    title: 'Strict Specification Compliance',
    desc: 'We strictly develop in accordance with your contract specifications, ensuring every deliverable meets precise performance, design, and regulatory requirements.',
  },
  {
    icon: ShieldCheck,
    title: 'Tailored Client Solutions',
    desc: 'No rigid packages. We adapt entirely to the unique feature requirements, design languages, and technical stacks required by your enterprise end-clients.',
  },
];

const About = () => {
  return (
    <main className="bg-white">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 -z-10"
          style={{ background: 'linear-gradient(135deg, #fde7ee 0%, #e8f2fc 50%, #f6e7fb 100%)' }}
        />
        <svg
          className="absolute inset-0 w-full h-full -z-10 opacity-70"
          preserveAspectRatio="none"
          viewBox="0 0 1200 500"
          fill="none"
        >
          <g stroke="#e5a4b7" strokeWidth="0.6" opacity="0.55">
            <line x1="40" y1="120" x2="180" y2="60" />
            <line x1="180" y1="60" x2="260" y2="200" />
            <line x1="960" y1="80" x2="1080" y2="180" />
            <line x1="1080" y1="180" x2="1160" y2="320" />
            <line x1="1050" y1="260" x2="940" y2="380" />
          </g>
          {[[40, 120], [180, 60], [260, 200], [960, 80], [1080, 180], [1160, 320], [1050, 260], [940, 380], [600, 40], [720, 440]].map(
            ([cx, cy], i) => (
              <circle key={i} cx={cx} cy={cy} r={i % 3 === 0 ? 4 : 2.5} fill={i % 2 === 0 ? '#e63946' : '#f4a3b6'} opacity={0.8} />
            )
          )}
        </svg>

        <div className="max-w-6xl mx-auto px-5 lg:px-8 pt-20 pb-24 text-center">
          <div className="inline-flex items-center gap-2 bg-white/70 backdrop-blur border border-white/80 rounded-full px-4 py-1.5 text-xs font-semibold text-[#E63946] shadow-sm">
            About Kamikatazi Corp
          </div>
          <h1 className="mt-6 text-[36px] md:text-[56px] lg:text-[64px] leading-[1.05] font-bold text-slate-900 tracking-tight">
            Front-End Engineering <span className="text-[#E63946]">Partners</span> for Scale
          </h1>
          <p className="mt-6 max-w-3xl mx-auto text-slate-700 text-base md:text-lg leading-relaxed">
            We provide elite front-end software development on a selective subcontractor basis, partnering directly with
            large-scale software development firms to deliver high-performance, enterprise-grade SaaS applications. We bridge
            the gap between complex backend architectures and flawless, user-centric interfaces, ensuring your largest clients
            get exactly what they need.
          </p>
          <Link
            to="/#contact"
            className="inline-flex items-center gap-2 mt-10 bg-[#E63946] hover:bg-[#d02b38] text-white font-semibold px-8 py-4 rounded-full text-[15px] shadow-lg shadow-red-200 transition-colors"
          >
            Discuss an Engagement <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="max-w-6xl mx-auto px-5 lg:px-8 pt-10">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Link to="/" className="hover:text-[#E63946]">Home</Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-[#E63946]">About</span>
        </div>
      </div>

      {/* What We Do */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="max-w-3xl">
            <h2 className="text-[30px] md:text-[42px] font-bold text-slate-900 leading-tight">What We Do</h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              We specialize in executing technical front-end requirements for massive software ecosystems. Our team integrates
              seamlessly into your existing workflows to engineer specialized, high-fidelity user interfaces according to your
              precise project specifications.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
            {whatWeDo.map((item) => (
              <div
                key={item.title}
                className="bg-white rounded-2xl p-7 border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.04)] hover:shadow-[0_16px_40px_rgba(230,57,70,0.1)] hover:-translate-y-1 transition-all duration-300"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-6 h-6 text-[#E63946]" />
                  </div>
                  <div>
                    <h3 className="text-[19px] font-bold text-slate-900 leading-snug">{item.title}</h3>
                    <p className="mt-2 text-slate-600 text-[15px] leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Engagement Terms */}
      <section className="py-20 bg-[#f6f7fb]">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="max-w-3xl">
            <h2 className="text-[30px] md:text-[42px] font-bold text-slate-900 leading-tight">Engagement Terms</h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              We work with prime development agencies who value precision engineering, reliability, and elite technical
              capability. Our engagement structure is designed for straightforward integration into enterprise project budgets.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl p-8 border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.05)]">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-red-50 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-[#E63946]" />
                </div>
                <span className="text-sm uppercase tracking-widest text-slate-500 font-semibold">Billable Hourly Rate</span>
              </div>
              <div className="mt-6 flex items-baseline gap-2">
                <span className="text-[56px] md:text-[64px] font-black text-slate-900 leading-none">$270</span>
                <span className="text-slate-500 font-semibold">/ hour</span>
              </div>
              <p className="mt-4 text-slate-600 text-sm">
                Transparent, fixed hourly rate for senior front-end engineering aligned to enterprise project budgets.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.05)]">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-red-50 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-[#E63946]" />
                </div>
                <span className="text-sm uppercase tracking-widest text-slate-500 font-semibold">Minimum Engagement</span>
              </div>
              <div className="mt-6 flex items-baseline gap-2">
                <span className="text-[56px] md:text-[64px] font-black text-slate-900 leading-none">80</span>
                <span className="text-slate-500 font-semibold">billable hours / contract</span>
              </div>
              <p className="mt-4 text-slate-600 text-sm">
                A focused minimum that ensures deep integration, meaningful delivery, and measurable outcomes on every engagement.
              </p>
            </div>
          </div>

          <div className="mt-12 flex flex-col md:flex-row items-center justify-between gap-6 bg-white rounded-2xl border border-slate-100 p-8 shadow-[0_4px_20px_rgba(0,0,0,0.04)]">
            <div>
              <h3 className="text-2xl font-bold text-slate-900">Looking for a specialized front-end partner?</h3>
              <p className="mt-2 text-slate-600">
                Let's align on scope, specifications, and timelines for your next enterprise engagement.
              </p>
            </div>
            <Link
              to="/#contact"
              className="bg-[#E63946] hover:bg-[#d02b38] text-white font-semibold px-8 py-4 rounded-full transition-colors whitespace-nowrap"
            >
              Start a Conversation
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default About;
